import React, {useRef, useState} from 'react';
import {
  MediaFile,
  VimeoMediaFile,
} from '../../../../data/models/LibraryFile/MediaFile';
import {
  getMilisecondsFromTimeText,
  getTimeMilisecondsTextFromSeconds,
} from '../../../../utils/getTimeTextFromSeconds';
import SimplePlayerControlled from '../../../../uiToolkit/SimplePlayerControlled';
import './styles.css';
import TimeInput from '../../../../uiToolkit/Inputs/TimeInput/indes';
import Button from '../../../../uiToolkit/Buttons/Button';
import ModalContainer from '../../../../uiToolkit/ModalContainer';
import {addVideoOverlayWizardText} from '../addVideoOverlayWizardText';
import {
  OVERLAY_ACTION_SEEK,
  OVERLAY_NEXT_NODE_OPTION,
  OVERLAY_PREV_NODE_OPTION,
  overlayTapActionOptions,
} from '../../../../data/models/LessonContent/ContentFreeform';
import {
  OVERLAY_ACTION_URL,
  VideoOverlay,
} from '../../../../data/models/LessonContent/ContentFreeform';
import {Accordion} from 'react-bootstrap';
import {AccordionEventKey} from 'react-bootstrap/esm/AccordionContext';
import Tooltip from '../../../../uiToolkit/Tooltip';
import {
  LessonContentOverlayVideo,
  LessonContentVimeoOverlayVideo,
} from '../../../../data/models/LessonContent/LessonContentBase';
import {
  LessonContentList,
  getListOfItemsFromContentList,
} from '../../../../data/models/LessonContent/LessonContentList';
import {LessonShortModel} from '../../../../data/models/Course/Lesson';
import EditorSvgCanvas from '../../FreeformElements/SvgCanvas';
import EditorSvgPath from '../../FreeformElements/EditorSvgPath';
import OverlayOptions, {
  videoOverlayValidators,
} from '../components/OverlayOptions';
import {
  addPointToOverlay,
  generateShapePoints,
  removePointFromOverlay,
} from '../../FreeformElements/EditorSvgPath/utils';
import TablePagination from '../../../../uiToolkit/TablePagination';

interface Props {
  selectedFile: MediaFile | null;
  selectedVimeoFile: VimeoMediaFile | null;
  existingOverlayNode?:
    | LessonContentOverlayVideo
    | LessonContentVimeoOverlayVideo;
  onCancel: () => void;
  onSave: (start: number, end: number, overlays: VideoOverlay[]) => void;
  lessonContent?: LessonContentList;
  lessonsList: LessonShortModel[];
  isGradable: boolean;
  isLoading: boolean;
}

const OVERLAYS_PAGE_SIZE = 5;
const MAX_OVERLAYS = 15;

const CropVideoStepUI = React.memo(
  ({
    selectedFile,
    onCancel,
    onSave,
    selectedVimeoFile,
    existingOverlayNode,
    lessonContent,
    lessonsList,
    isLoading,
  }: Props) => {
    const [width, setWidth] = useState(0);
    const [timeStart, setTimeStart] = useState('00:00:00.000');
    const [timeEnd, setTimeEnd] = useState('00:00:01.000');
    const [duration, setDuration] = useState(0);
    const [progress, setProgress] = useState(0);
    const [overlays, setOverlays] = useState<VideoOverlay[]>([]);
    const [activeOverlayIndex, setActiveOverlayIndex] = useState<number | null>(
      null,
    );
    const [currentPage, setCurrentPage] = useState(1);
    const containerRef = useRef<any>(null);
    const scrollAnchor = useRef<any>(null);

    React.useEffect(() => {
      if (existingOverlayNode) {
        setTimeStart(
          getTimeMilisecondsTextFromSeconds(existingOverlayNode.startInSeconds),
        );
        setTimeEnd(
          getTimeMilisecondsTextFromSeconds(existingOverlayNode.endInSeconds),
        );
        setOverlays(existingOverlayNode.overlays);
        if (existingOverlayNode.overlays.length) {
          setActiveOverlayIndex(0);
        }
      }
    }, [existingOverlayNode, width]);

    React.useEffect(() => {
      function handleResize() {
        if (containerRef && containerRef.current) {
          setWidth(containerRef.current.offsetWidth);
        }
      }

      window.addEventListener('resize', handleResize);
      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }, []);

    React.useEffect(() => {
      if (overlays.length === 1) {
        setActiveOverlayIndex(0);
      }
    }, [overlays]);

    React.useLayoutEffect(() => {
      if (containerRef && containerRef.current) {
        setWidth(containerRef.current.offsetWidth);
      }
    }, []);

    const saveNode = () => {
      if (startTimeIsValid() && endTimeIsValid() && allOverlaysAreValid()) {
        onSave(
          getMilisecondsFromTimeText(timeStart),
          getMilisecondsFromTimeText(timeEnd),
          overlays.map(overlayVM => ({
            ...overlayVM,
            encodedAtWidth: width,
          })),
        );
      }
    };

    const onAddNewOverlay = () => {
      if (overlays.length >= MAX_OVERLAYS) {
        return;
      }
      const newOverlay: VideoOverlay = {
        overlayStartTime: 0.0,
        overlayEndTime: 1.0,
        overlayCoordinates: generateShapePoints(500),
        actionType: OVERLAY_ACTION_URL,
        encodedAtWidth: width,
        overlayVisible: false,
        overlayTitle: `New Overlay`,
      };
      const newOverlays = [...overlays, newOverlay];
      setOverlays(newOverlays);
    };

    const onDeleteOverlay = (index: number) => {
      const newOverlays = [...overlays];
      newOverlays.splice(index, 1);
      setOverlays(newOverlays);
      if (
        currentPage > Math.ceil(newOverlays.length / OVERLAYS_PAGE_SIZE) &&
        currentPage > 1
      ) {
        setCurrentPage(currentPage - 1);
      }
    };

    const onAddPoint = () => {
      if (activeOverlayIndex == null) return;
      const currentOverlay =
        overlays[activeOverlayIndex + (currentPage - 1) * OVERLAYS_PAGE_SIZE];
      if (currentOverlay) {
        const newPoints = addPointToOverlay(currentOverlay.overlayCoordinates);
        const updatedOverlay = {
          ...currentOverlay,
          overlayCoordinates: newPoints,
        };
        updateOverlayByIndex(
          activeOverlayIndex + (currentPage - 1) * OVERLAYS_PAGE_SIZE,
        )(updatedOverlay);
      }
    };

    const onRemovePoint = () => {
      if (activeOverlayIndex == null) return;
      const currentOverlay =
        overlays[activeOverlayIndex + (currentPage - 1) * OVERLAYS_PAGE_SIZE];
      if (currentOverlay) {
        const newPoints = removePointFromOverlay(
          currentOverlay.overlayCoordinates,
        );
        const updatedOverlay = {
          ...currentOverlay,
          overlayCoordinates: newPoints,
        };
        updateOverlayByIndex(
          activeOverlayIndex + (currentPage - 1) * OVERLAYS_PAGE_SIZE,
        )(updatedOverlay);
      }
    };

    const startTimeIsValid = () => {
      if (duration > 0) {
        const seconds = getMilisecondsFromTimeText(timeStart);
        const endSeconds = getMilisecondsFromTimeText(timeEnd);
        return seconds <= duration && seconds < endSeconds;
      }
      return true;
    };

    const endTimeIsValid = () => {
      if (duration > 0) {
        const seconds = getMilisecondsFromTimeText(timeEnd);
        return seconds <= duration;
      }
      return true;
    };

    const onDuration = (dur: number) => {
      setDuration(dur);
      if (timeEnd === '00:00:01.000' && !existingOverlayNode) {
        setTimeEnd(getTimeMilisecondsTextFromSeconds(dur));
      }
    };

    const updateActiveOverlayPoints = (value: any) => {
      if (activeOverlayIndex == null) return;
      const currentOverlay =
        overlays[activeOverlayIndex + (currentPage - 1) * OVERLAYS_PAGE_SIZE];
      if (currentOverlay) {
        const updatedOverlay: VideoOverlay = {
          ...currentOverlay,
          overlayCoordinates: value,
        };
        updateOverlayByIndex(
          activeOverlayIndex + (currentPage - 1) * OVERLAYS_PAGE_SIZE,
        )(updatedOverlay);
      }
    };

    const updateOverlayByIndex = (index: number) => (overlay: VideoOverlay) => {
      if (index == null) return;
      const updatedOverlays = [...overlays];
      updatedOverlays[index] = overlay;
      setOverlays(updatedOverlays);
    };

    const getCurrentOverlay = () => {
      if (activeOverlayIndex == null) return;
      const currentOverlay =
        overlays[activeOverlayIndex + (currentPage - 1) * OVERLAYS_PAGE_SIZE];
      if (
        currentOverlay &&
        currentOverlay.overlayStartTime <= progress &&
        currentOverlay.overlayEndTime >= progress
      ) {
        return currentOverlay;
      }
    };

    const overlayIsValid = (index: number) => {
      const overlay = overlays[index];
      if (!overlay) return false;

      const validators = videoOverlayValidators(
        overlay,
        getMilisecondsFromTimeText(timeStart),
        getMilisecondsFromTimeText(timeEnd),
      );

      if (!validators.overlayStartTimeIsValid()) {
        return false;
      }

      if (!validators.overlayEndTimeIsValid()) {
        return false;
      }

      if (overlay.actionType === OVERLAY_ACTION_URL && !overlay.payload) {
        return false;
      }

      if (
        overlay.actionType === OVERLAY_ACTION_SEEK &&
        !validators.overlaySeekTimeIsValid()
      ) {
        return false;
      }

      return true;
    };

    const onSelectOverlay = (item: AccordionEventKey) => {
      if (item && !Array.isArray(item)) {
        setActiveOverlayIndex(parseInt(item, 10));
      }
    };

    const allOverlaysAreValid = () => {
      let allValid = true;
      overlays.forEach((overlay, index) => {
        if (!overlayIsValid(index)) {
          allValid = false;
        }
      });
      return allValid;
    };

    const getSource = () => {
      if (selectedFile) {
        return selectedFile.fileUrl;
      }
      if (selectedVimeoFile) {
        return selectedVimeoFile.url;
      }
      if (existingOverlayNode) {
        return existingOverlayNode.contentUrl;
      }
    };

    const onDropdownToggle = (isOpen: boolean) => {
      if (isOpen && scrollAnchor && scrollAnchor.current) {
        setTimeout(() => {
          scrollAnchor.current.scrollIntoView({behavior: 'smooth'});
        }, 100);
      }
    };

    return (
      <ModalContainer onClose={onCancel}>
        <div className="d-flex flex-row">
          <div className="AddVideoOverlaySourceStepContent CropPlayerContent">
            <p className="CropHeader">{addVideoOverlayWizardText.cropTitle}</p>
            <div className="CropPlayerContainer" ref={containerRef}>
              <SimplePlayerControlled
                width={width}
                onDuration={onDuration}
                onVideoProgress={setProgress}
                showFullscreen
                source={getSource()}
                startTime={
                  startTimeIsValid()
                    ? getMilisecondsFromTimeText(timeStart)
                    : undefined
                }
                endTime={
                  endTimeIsValid()
                    ? getMilisecondsFromTimeText(timeEnd)
                    : undefined
                }
              />
              {getCurrentOverlay() && (
                <EditorSvgCanvas width={width} showGuidelines>
                  <EditorSvgPath
                    shapePoints={getCurrentOverlay()!.overlayCoordinates}
                    updateShapePoints={updateActiveOverlayPoints}
                    width={width}
                  />
                </EditorSvgCanvas>
              )}
            </div>
            <div className="TimePickersContainer">
              <TimeInput
                value={timeStart}
                onChange={setTimeStart}
                label={addVideoOverlayWizardText.startTime}
                isValid={startTimeIsValid()}
                labelTop
                includeMiliseconds
              />
              <TimeInput
                value={timeEnd}
                onChange={setTimeEnd}
                label={addVideoOverlayWizardText.endTime}
                isValid={endTimeIsValid()}
                labelTop
                includeMiliseconds
              />
            </div>
            <div className="Footer">
              <Button
                title={addVideoOverlayWizardText.cancel}
                onClick={onCancel}
                uiType="text"
              />
              <Button
                title={addVideoOverlayWizardText.save}
                onClick={saveNode}
                isLoading={isLoading}
                disabled={
                  !startTimeIsValid() ||
                  !endTimeIsValid() ||
                  !allOverlaysAreValid()
                }
                uiType="action"
              />
            </div>
          </div>
          <div className="OverlaySettings">
            <p className="Title">{addVideoOverlayWizardText.overlayTitle}</p>
            <Button
              title={addVideoOverlayWizardText.addOverlay}
              onClick={onAddNewOverlay}
              uiType="box"
              className="AddOverlayButton"
              disabled={overlays.length >= MAX_OVERLAYS}
            />
            <div className="OverlaysList">
              <Accordion defaultActiveKey={'0'} onSelect={onSelectOverlay}>
                {overlays
                  .slice(
                    (currentPage - 1) * OVERLAYS_PAGE_SIZE,
                    (currentPage - 1) * OVERLAYS_PAGE_SIZE + OVERLAYS_PAGE_SIZE,
                  )
                  .map((item, index) => (
                    <Accordion.Item eventKey={index.toString()} key={index}>
                      <Accordion.Header>
                        <div className="OverlayHeader">
                          {item.overlayTitle}
                          {!overlayIsValid(
                            index + (currentPage - 1) * OVERLAYS_PAGE_SIZE,
                          ) && (
                            <Tooltip
                              tooltipText={
                                addVideoOverlayWizardText.overlayError
                              }>
                              <span className="icon-typeWarning OverlayWarning"></span>
                            </Tooltip>
                          )}
                        </div>
                      </Accordion.Header>
                      <Accordion.Body>
                        <OverlayOptions
                          overlay={item}
                          updateOverlay={updateOverlayByIndex(
                            index + (currentPage - 1) * OVERLAYS_PAGE_SIZE,
                          )}
                          videoTimeStart={getMilisecondsFromTimeText(timeStart)}
                          videoTimeEnd={getMilisecondsFromTimeText(timeEnd)}
                          onAddPoint={onAddPoint}
                          onRemovePoint={onRemovePoint}
                          actionOptions={overlayTapActionOptions()}
                          lessonContentOptions={[
                            OVERLAY_PREV_NODE_OPTION,
                            ...getListOfItemsFromContentList(lessonContent),
                            OVERLAY_NEXT_NODE_OPTION,
                          ]}
                          courseLessonsOptions={lessonsList.map(item => ({
                            key: item.lessonId,
                            value: item.lessonName,
                          }))}
                          onDropDownToggle={onDropdownToggle}
                          onDeleteOverlay={() =>
                            onDeleteOverlay(
                              (currentPage - 1) * OVERLAYS_PAGE_SIZE + index,
                            )
                          }
                        />
                      </Accordion.Body>
                    </Accordion.Item>
                  ))}
              </Accordion>
              {overlays.length >= OVERLAYS_PAGE_SIZE && (
                <TablePagination
                  rowsPerPage={OVERLAYS_PAGE_SIZE}
                  rowCount={overlays.length}
                  onChangePage={setCurrentPage}
                  currentPage={currentPage}
                />
              )}
              <div ref={scrollAnchor} />
            </div>
          </div>
        </div>
      </ModalContainer>
    );
  },
);

export default CropVideoStepUI;
